import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useGroups } from '../contexts/GroupContext';

export default function GroupForm({ open, onClose, group = null, mode = 'create' }) {
  const { createGroup, updateGroup } = useGroups();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'private'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Initialize form with group data if editing
  useEffect(() => {
    if (group && mode === 'edit') {
      setFormData({
        name: group.name || '',
        description: group.description || '',
        privacy: group.privacy || 'private'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        privacy: 'private'
      });
    }
    setErrors({});
    setSubmitError('');
  }, [group, mode, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Group name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Group name must be less than 50 characters';
    }

    if (formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      if (mode === 'create') {
        await createGroup(formData);
      } else {
        await updateGroup(group.id, formData);
      }
      
      onClose();
    } catch (error) {
      console.error('GroupForm: Error submitting form:', error);
      setSubmitError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = formData.name.trim() && formData.description.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        {mode === 'create' ? 'Create New Group' : 'Edit Group'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Group Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Enter a name for your group (2-50 characters)'}
            margin="normal"
            required
            disabled={loading}
            autoFocus
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'Describe your group (optional, max 500 characters)'}
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
          />

          <FormControl fullWidth margin="normal" disabled={loading}>
            <InputLabel>Privacy Level</InputLabel>
            <Select
              value={formData.privacy}
              label="Privacy Level"
              onChange={(e) => handleInputChange('privacy', e.target.value)}
            >
              <MenuItem value="private">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">🔒 Private</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only members can see this group
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="friends">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">👥 Friends Only</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Visible to friends of members
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="public">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">🌍 Public</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Visible to everyone (not recommended)
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Groups are completely private by default. Only invited members can join and see group content.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isFormValid}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading 
              ? (mode === 'create' ? 'Creating...' : 'Saving...') 
              : (mode === 'create' ? 'Create Group' : 'Save Changes')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
